/**
 * @flow
 */

import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon } from 'semantic-ui-react';
import { FormattedMessage } from 'react-intl';

import { GET_SUPPLIERS_QUERY, DELETE_SUPPLIER_QUERY } from '../containers/queries';
import type { ModalDialog, Supplier } from '../../../type';

type DeleteSupplierDialogProps = {
    supplier: Supplier,
} & ModalDialog;

const DeleteSupplierDialog = ({
    supplier,
    trigger,
    open,
    onClose,
}: DeleteSupplierDialogProps) => {
    const searchQuery = useSelector(({ suppliers }) =>
        suppliers.get('searchQuery'),
    );

    const [deleteSupplier, { loading }] = useMutation(DELETE_SUPPLIER_QUERY);
    const [deleteFailed, setDeleteFailed] = useState(false);

    const onDelete = useCallback(() => {
        setDeleteFailed(false);
        deleteSupplier({
            variables: {
                input: {
                    supplierId: supplier.id,
                },
            },
            // Remove the deleted supplier from the cached list query
            update: (store, { data: { deleteSupplier: result } }) => {
                const variables = {
                    search: searchQuery || undefined,
                    first: 20,
                    resolveRole: 'SYSADMIN',
                };

                const data = store.readQuery({
                    query: GET_SUPPLIERS_QUERY,
                    variables,
                });

                if (data && data.viewer && data.viewer.suppliers && data.viewer.suppliers.edges) {
                    store.writeQuery({
                        query: GET_SUPPLIERS_QUERY,
                        variables,
                        data: {
                            ...data,
                            viewer: {
                                ...data.viewer,
                                suppliers: {
                                    ...data.viewer.suppliers,
                                    edges: data.viewer.suppliers.edges.filter(
                                        edge => edge.node.id !== result.supplierId,
                                    ),
                                },
                            },
                        },
                    });
                }
            },
        })
            .then(() => {
                onClose();
            })
            .catch(() => {
                setDeleteFailed(true);
            });
    }, [deleteSupplier, supplier, searchQuery, onClose]);

    return (
        <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
            <Header icon>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.suppliers.deleteSupplier.deleteHeader"
                    defaultMessage="Delete Supplier"
                />
            </Header>
            <Modal.Content>
                <p>
                    <FormattedMessage
                        id="pages.suppliers.deleteSupplier.deleteText"
                        defaultMessage="Are you sure you want to delete {firstName} {lastName}?"
                        values={{
                            firstName: supplier.firstName,
                            lastName: supplier.lastName,
                        }}
                    />
                </p>
                {deleteFailed && (
                    <p>
                        <FormattedMessage
                            id="errors.defaultMessage"
                            defaultMessage="Something went wrong"
                        />
                    </p>
                )}
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose} color="red" basic inverted disabled={loading}>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.suppliers.deleteSupplier.button.no"
                        defaultMessage="No"
                    />
                </Button>
                <Button
                    onClick={onDelete}
                    color="green"
                    inverted
                    loading={loading}
                    disabled={loading}
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.suppliers.deleteSupplier.button.yes"
                        defaultMessage="Yes"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default DeleteSupplierDialog;
